import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "./AuditOverview.module.scss";
import AuditService, {
  AuditFilterParams,
} from "../../../api/services/audit.service";
import { useCurrencies } from "../../../context/CurrenciesContext";

interface Props {
  filters: {
    storeId: string;
    startDate: string;
    endDate: string;
  };
}

interface Totals {
  gmv: number;
  platform_profit: number;
  platform_margin_net: number;
  platform_correct_margin: number;
  tax_total: number;
  expenses_total: number;
  paid_to_stores: number;
  not_yet_paid_to_stores: number;
  tax_paid: number;
  tax_unpaid: number;
}

const AuditOverview = ({ filters }: Props) => {
  const { t } = useTranslation("translation");
  const [totals, setTotals] = useState<Totals | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const { currencyId, currency } = useCurrencies();

  const formatMoney = (val?: number) =>
    `${(val ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency || ""}`;

  useEffect(() => {
    const fetchOverview = async () => {
      try {
        setLoading(true);
        const params: AuditFilterParams = {};
        if (filters.startDate) params.start_date = filters.startDate;
        if (filters.endDate) params.end_date = filters.endDate;
        if (filters.storeId) params.store_id = filters.storeId;
        if (currencyId) params.currency_id = currencyId;
        const res = await AuditService.getOverview(params);
        setTotals(res.data?.totals || null);
      } catch (e) {
        setTotals(null);
      } finally {
        setLoading(false);
      }
    };

    fetchOverview();
  }, [filters, currencyId]);

  return (
    <div className={styles.report_analytics}>
      <div className={styles.header}>
        <h1 className={styles.title}>{t("audit_overview.title")}</h1>
        <p className={styles.subtitle}>{t("audit_overview.subtitle")}</p>
      </div>
      <div className={styles.all_wrapper}>
        <div className={styles.cards_wrapper}>
          <div className={styles.cards}>
            <div className={styles.card}>
              <div className={styles.info}>
                <p className={styles.card_title}>
                  {t("audit_overview.total_gmv.title")}
                </p>
                <p className={styles.card_subtitle}>
                  {t("audit_overview.total_gmv.subtitle")}
                </p>
              </div>
              <p className={styles.amount}>
                {loading ? t("common.loading_dots") : formatMoney(totals?.gmv)}
              </p>
            </div>
            <div className={styles.card}>
              <div className={styles.info}>
                <p className={styles.card_title}>
                  {t("audit_overview.platform_profit.title")}
                </p>
                <p className={styles.card_subtitle}>
                  {t("audit_overview.platform_profit.subtitle")}
                </p>
              </div>
              <p className={styles.amount}>
                {loading
                  ? t("common.loading_dots")
                  : formatMoney(totals?.platform_profit)}
              </p>
            </div>
            <div className={styles.card}>
              <div className={styles.info}>
                <p className={styles.card_title}>
                  {t("audit_overview.platform_margin.title")}
                </p>
                <p className={styles.card_subtitle}>
                  {t("audit_overview.platform_margin.subtitle")}
                </p>
              </div>
              <p className={styles.amount}>
                {loading
                  ? t("common.loading_dots")
                  : formatMoney(totals?.platform_margin_net)}
              </p>
            </div>
          </div>
          <div className={styles.card}>
            <div className={styles.info}>
              <p className={styles.card_title}>
                {t("audit_overview.paid_to_stores.title")}
              </p>
              <p className={styles.card_subtitle}>
                {t("audit_overview.paid_to_stores.subtitle")}
              </p>
            </div>
            <p className={styles.amount}>
              {loading
                ? t("common.loading_dots")
                : formatMoney(totals?.paid_to_stores)}
            </p>
          </div>
        </div>
        <div className={styles.cards_wrapper}>
          <div className={styles.cards}>
            <div className={styles.card}>
              <div className={styles.info}>
                <p className={styles.card_title}>
                  {t("audit_overview.total_tax.title")}
                </p>
                <p className={styles.card_subtitle}>
                  {t("audit_overview.total_tax.subtitle")}
                </p>
              </div>
              <p className={styles.amount}>
                {loading
                  ? t("common.loading_dots")
                  : formatMoney(totals?.tax_total)}
              </p>
            </div>
            <div className={styles.card}>
              <div className={styles.info}>
                <p className={styles.card_title}>
                  {t("audit_overview.unpaid_tax.title")}
                </p>
                <p className={styles.card_subtitle}>
                  {t("audit_overview.unpaid_tax.subtitle")}
                </p>
              </div>
              <p className={styles.amount}>
                {loading
                  ? t("common.loading_dots")
                  : formatMoney(totals?.tax_unpaid)}
              </p>
            </div>
            <div className={styles.card}>
              <div className={styles.info}>
                <p className={styles.card_title}>
                  {t("audit_overview.store_remaining.title")}
                </p>
                <p className={styles.card_subtitle}>
                  {t("audit_overview.store_remaining.subtitle")}
                </p>
              </div>
              <p className={styles.amount}>
                {loading
                  ? t("common.loading_dots")
                  : formatMoney(totals?.not_yet_paid_to_stores)}
              </p>
            </div>
          </div>
          <div className={styles.card}>
            <div className={styles.info}>
              <p className={styles.card_title}>
                {t("audit_overview.not_yet_paid.title")}
              </p>
              <p className={styles.card_subtitle}>
                {t("audit_overview.not_yet_paid.subtitle")}
              </p>
            </div>
            <p className={styles.amount}>
              {loading
                ? t("common.loading_dots")
                : formatMoney(totals?.not_yet_paid_to_stores)}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuditOverview;
